import { useState, useEffect } from "react";
import { ChevronUp, Mail, HelpCircle, ChevronDown } from "lucide-react";

const faqs = [
  {
    id: "how-it-works",
    question: "How does Video Inspect work?",
    answer: (
      <p>
        Pick a video from your library and Video Inspect indexes it privately on your device.
        Once indexed, you can search inside it by typing what you are looking for and jump
        straight to that moment.
      </p>
    ),
  },
  {
    id: "videos-leave-device",
    question: "Do my videos ever leave my device?",
    answer: (
      <p>
        No. All indexing and searching happens entirely on your iPhone. Your videos are never
        uploaded, and there is no account or cloud.
      </p>
    ),
  },
  {
    id: "free-limit",
    question: "How many videos can I index for free?",
    answer: (
      <p>
        You can index 2 videos for free. To index more, you can unlock unlimited indexing with
        a single one-time purchase.
      </p>
    ),
  },
  {
    id: "restore-purchase",
    question: "I purchased the unlock but it is not active. What do I do?",
    answer: (
      <p>
        Open the app, go to the paywall or Settings, and tap Restore Purchase. If it still
        does not activate, email us at the address above and we will help.
      </p>
    ),
  },
  {
    id: "indexed-videos",
    question: "Will my already indexed videos stay searchable?",
    answer: (
      <p>
        Yes. Videos you have already indexed stay fully searchable, even on the free tier. The
        unlock only lets you index more videos.
      </p>
    ),
  },
  {
    id: "import-issue",
    question: "A video would not import. What happened?",
    answer: (
      <p>
        This usually means the video is still downloading from iCloud or the connection
        dropped. Make sure the video is fully downloaded to your device, keep the app open, and
        try again.
      </p>
    ),
  },
];

const FaqItem = ({ faq }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="sp-faq-item" data-testid={`vi-faq-item-${faq.id}`}>
      <button
        className={`sp-faq-question ${open ? "open" : ""}`}
        onClick={() => setOpen(!open)}
        data-testid={`vi-faq-toggle-${faq.id}`}
        aria-expanded={open}
      >
        <span>{faq.question}</span>
        <ChevronDown size={18} className={`sp-faq-chevron ${open ? "rotated" : ""}`} />
      </button>
      {open && (
        <div className="sp-faq-answer" data-testid={`vi-faq-answer-${faq.id}`}>
          {faq.answer}
        </div>
      )}
    </div>
  );
};

const VideoInspectSupport = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="pp-page" data-testid="videoinspect-support-page">
      <main className="pp-main" data-testid="videoinspect-support-content">
        <article className="pp-article">
          <header className="pp-header" data-testid="videoinspect-support-header">
            <div className="pp-header-icon pp-header-icon--support">
              <HelpCircle size={28} strokeWidth={1.5} />
            </div>
            <h1 className="pp-title">Video Inspect Support</h1>
            <p className="sp-subtitle">
              Need help with Video Inspect? We are here for you.
            </p>
          </header>

          <section className="pp-section" data-testid="section-vi-support-contact">
            <h2 className="pp-section-heading">Contact</h2>
            <div className="sp-email-card" data-testid="vi-support-email-card">
              <a
                href="mailto:support@radships.com"
                className="sp-email-link"
                data-testid="vi-support-email-link"
              >
                <Mail size={20} />
                support@radships.com
              </a>
            </div>
            <p>We typically respond within 2 to 3 business days.</p>
          </section>

          <section className="pp-section" data-testid="section-vi-faqs">
            <h2 className="pp-section-heading">Frequently Asked Questions</h2>
            <div className="sp-faq-list" data-testid="vi-faq-list">
              {faqs.map((faq) => (
                <FaqItem key={faq.id} faq={faq} />
              ))}
            </div>
          </section>

          <section className="pp-section" data-testid="section-vi-support-privacy">
            <h2 className="pp-section-heading">Privacy</h2>
            <p>
              Read our privacy policy at{" "}
              <a
                href="/videoinspect/privacy"
                className="pp-link"
                data-testid="vi-support-privacy-link"
              >
                radships.com/videoinspect/privacy
              </a>
            </p>
          </section>

          <footer className="pp-footer" data-testid="videoinspect-support-footer">
            <p>&copy; {new Date().getFullYear()} Radhesh Gowd. All rights reserved.</p>
          </footer>
        </article>
      </main>

      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="pp-back-to-top"
          aria-label="Back to top"
          data-testid="vi-support-back-to-top"
        >
          <ChevronUp size={20} />
        </button>
      )}
    </div>
  );
};

export default VideoInspectSupport;
